/* Copyright (c) 2020-present CloudIO Inc */

import { IoDatasource } from '@cloudio-saas/datasource-types';
import { useStateXAtom, useStateXAtomSetter } from '@cloudio-saas/statex';
import { Alert, Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import React from 'react';
import useDataStore from 'store/useDataStore';
import {
  getIoDatasourceStoreProps,
  useIoDatasourceAttributesStore,
  useIoDatasourceEditStore,
} from 'store/utils/utils';
import {
  DSView,
  sidebarDatasourceEditModeAtom,
  sidebarDataSourceWizardTitleAtom,
} from '../../../atoms';
import { WizardOption } from '../../common';
import { sidebarDatasourceUploadFileAtom } from './DataSourceWizardTypes';
import DataSourceWizardUpload from './DataSourceWizardUpload';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    position: 'absolute',
    bottom: 0,
    top: 0,
    left: 0,
    right: 0,
    overflowX: 'hidden',
    overflowY: 'auto',
    gap: '16px',
    backgroundColor: theme.palette.background.default,
  },
}));

const DataSourceWizard = ({
  setView,
  appUid,
  roleUid,
  width,
}: {
  width: number;
  appUid: string;
  roleUid: string;
  setView: (_view: DSView) => void;
}): React.ReactElement | null => {
  const classes = useStyles();
  const setTitle = useStateXAtomSetter(sidebarDataSourceWizardTitleAtom);
  const listStore = useDataStore<IoDatasource>(
    getIoDatasourceStoreProps(appUid),
  );

  const editStore = useIoDatasourceEditStore(appUid);
  const attrStore = useIoDatasourceAttributesStore(appUid);
  const setEditMode = useStateXAtomSetter(sidebarDatasourceEditModeAtom);
  const [uploadFile, setUploadFile] = useStateXAtom(
    sidebarDatasourceUploadFileAtom,
  );

  if (uploadFile) {
    return (
      <DataSourceWizardUpload
        setView={setView}
        appUid={appUid}
        roleUid={roleUid}
        accept={uploadFile}
        width={width}
      />
    );
  }

  return (
    <div className={classes.root}>
      {appUid === 'cloudio' ? (
        <Alert variant="filled" severity="warning">
          You are trying to create a DataSource in cloudio schema! You may want
          to create inside your own application instead.
        </Alert>
      ) : null}
      <WizardOption
        data-testid="table"
        iconName="database"
        title="From existing Table"
        description="Auto Generate a new DataSource from an existing database Table"
        onClick={async () => {
          await attrStore.clear();
          await editStore.clear();
          await listStore.createNew({
            partialRecord: {
              datasourceType: 'Table',
              editMode: 'Table',
              appUid,
            },
            rs: 'I',
          });
          await editStore.createNew({
            partialRecord: {
              datasourceType: 'Table',
              editMode: 'Table',
              appUid,
            },
            rs: 'I',
          });
          setEditMode('Table');
          setView('edit');
        }}
      />
      <WizardOption
        data-testid="csv"
        iconName="file-csv"
        title="From a CSV File (.csv.zip)"
        description="Auto Generate a new DataSource by uploading a zip file containing a single CSV file"
        onClick={async () => {
          setUploadFile('csv');
          setEditMode('File');
          setTitle('Upload a CSV File');
        }}
      />
      <WizardOption
        data-testid="blank"
        iconName="edit"
        title="From Scratch"
        description="Manually defined a DataSource from scratch."
        onClick={async () => {
          await attrStore.clear();
          await editStore.clear();
          await listStore.createNew({
            partialRecord: {
              datasourceType: 'Table',
              editMode: 'Scratch',
              appUid,
            },
            rs: 'I',
          });
          await editStore.createNew({
            partialRecord: {
              datasourceType: 'Table',
              editMode: 'Scratch',
              appUid,
            },
            rs: 'I',
          });
          setEditMode('Scratch');
          setView('edit');
        }}
      />
      <WizardOption
        data-testid="sql"
        iconName="io-sql:cloudio"
        title="From SQL"
        description="Auto Generate a new DataSource from the given SQL"
        onClick={async () => {
          await attrStore.clear();
          await editStore.clear();
          await listStore.createNew({
            partialRecord: {
              datasourceType: 'Table',
              editMode: 'SQL',
              appUid,
            },
            rs: 'I',
          });
          await editStore.createNew({
            partialRecord: {
              datasourceType: 'Table',
              editMode: 'SQL',
              appUid,
            },
            rs: 'I',
          });
          setEditMode('SQL');
          setView('edit');
        }}
      />
    </div>
  );
};

export default DataSourceWizard;
